import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { useDispatch } from 'react-redux';
import { buildURL, get } from 'Shared/fetch';

import { useDefaultLandingPath } from '@/components/LayoutDetector/hooks';
import type { ProductSelection } from '@/components/products/CheckoutHandlerContext';
import useEligibilityBlocked from '@/components/products/useEligibilityBlocked';
import { AuthPopupProvider } from '@/components/SocialLoginBar/AuthPopupContext';
import { postUserProductMutationFn, productsQueryKeyPrefix } from '@/queries/products';
import { useSelector, useStore } from '@/redux/helper';
import { setUserData } from '@/redux/slices/user/actions';
import { withPopupMarker } from '@/routes/checkout/popup';
import { savePostAuthDestination } from '@/shared/postAuthDestination';

import { stepLabels } from './copy';
import { checkoutFailureEvent } from './core/checkoutFailure';
import { isChoiceStale } from './core/choice';
import { runDeferredCheckout } from './core/deferredCheckout';
import { satisfiesGate } from './core/entitlement';
import { withResumeState } from './core/resumeState';
import { CoveringModalContext } from './coveringModal';
import useCheckoutPopup from './hooks/useCheckoutPopup';
import useChosenProduct from './hooks/useChosenProduct';
import usePersistPersona from './hooks/usePersistPersona';
import useProductPairSync from './hooks/useProductPairSync';
import useReportFacts from './hooks/useReportFacts';
import useResumeState from './hooks/useResumeState';
import useSpeedyFlowAnalytics from './hooks/useSpeedyFlowAnalytics';
import useSsoPopup from './hooks/useSsoPopup';
import { takePendingAction } from './pendingAction';
import speedyFlowSlice from './redux/slice';
import selectSpeedyFlowUser from './selectUser';
import SpeedyFlowShell from './SpeedyFlowShell';
import AwaitPaymentStep from './steps/AwaitPaymentStep';
import ChooseProductStep from './steps/ChooseProductStep';
import CompletionStep from './steps/CompletionStep';
import LogInStep from './steps/LogInStep';
import PersonaStep from './steps/PersonaStep';
import SignUpStep from './steps/SignUpStep';
import VerifyEmailStep from './steps/VerifyEmailStep';
import { GateRequirement } from './types';

const { advanceSpeedyFlow, closeSpeedyFlow } = speedyFlowSlice.actions;

/**
 * The Speedy Flow modal: renders whichever step the machine is on, inside the responsive shell.
 *
 * It owns the checkout popup because the popup outcome is a machine event, not a step concern —
 * the choose-product step only hands over a URL, and what happens after belongs to the flow.
 */
const SpeedyFlow = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const queryClient = useQueryClient();
  const { visible, machine, gate, ppt, resumedChoice } = useSelector((state) => state.modals.speedyFlow);
  const [waitingForPayment, setWaitingForPayment] = useState(false);

  useSpeedyFlowAnalytics();
  useProductPairSync();

  /**
   * Refetch the user and ask whether the gate that opened this flow would now let them through.
   * That is the only thing that counts as proof of payment (ADR 0003) — not the redirect, and not
   * the tab closing.
   */
  const checkEntitlement = useCallback(async () => {
    if (!gate) {
      return false;
    }
    try {
      const user = await get(buildURL('/users/user'));
      // Written to redux and then read back through the selector, rather than projected a second
      // time from the raw response. The dispatch is synchronous, so the answer is available on the
      // next line — and it is the *same* projection that decided to open this flow in the first
      // place, so the gate cannot be answered two ways for one visitor.
      dispatch(setUserData(user));
      return satisfiesGate(gate, selectSpeedyFlowUser(store.getState()));
    } catch (err) {
      console.error('Could not check entitlement', err);
      return false;
    }
  }, [dispatch, gate, store]);

  const {
    open: openCheckout,
    reserve: reserveCheckoutTab,
    release: releaseCheckoutTab,
    retry,
    stop: stopCheckout,
  } = useCheckoutPopup({
    checkEntitlement,
    onConfirmed: () => {
      setWaitingForPayment(false);
      dispatch(advanceSpeedyFlow({ type: 'paymentConfirmed' }));
    },
    onUnconfirmed: () => {
      setWaitingForPayment(false);
      dispatch(advanceSpeedyFlow({ type: 'paymentUnconfirmed' }));
    },
    onAbandoned: () => {
      setWaitingForPayment(false);
      dispatch(advanceSpeedyFlow({ type: 'paymentAbandoned' }));
    },
  });

  /**
   * Open the Stripe tab for a URL we already have. Used once there is an account to bill.
   */
  const openCheckoutTab = useCallback(
    (url: string, selection: ProductSelection) => {
      // A card that built its own session hands the URL straight here, so the machine has not been
      // told a product was chosen yet. Move it now, or the modal would sit on the product list
      // while Stripe is open in the other tab.
      //
      // The selection has to come with it. This used to record empty ids, which meant a signed-in
      // member's confirmation card could not name what they had just bought and fell back to
      // "your new pass" — the guest path named it, so the two disagreed.
      dispatch(advanceSpeedyFlow({ type: 'choseProduct', ...selection }));
      setWaitingForPayment(true);
      if (!openCheckout(url)) {
        // Popup blocked, and there is no way to keep this page alive. The choice rides back on the
        // return path so the flow can be restored — see `resumeState` for why that is safe.
        window.location.href = url;
      }
    },
    [openCheckout, dispatch],
  );

  /**
   * A signed-out visitor picked a product. Record it and let the machine send them to sign up —
   * a checkout session cannot exist before the account does.
   */
  const onSelectProduct = useCallback(
    (selection: ProductSelection) => {
      dispatch(advanceSpeedyFlow({ type: 'choseProduct', ...selection }));
    },
    [dispatch],
  );

  /**
   * Once the flow reaches the payment step with a choice made earlier but no session yet, create
   * one now. This is the guest path catching up: the product was chosen before the account existed.
   */
  const startedCheckoutRef = useRef(false);
  const productChoice = machine?.productChoice;
  const atPayment = machine?.step === 'await-payment';
  /** A choice made before there was an account to bill; checkout has not begun. */
  const needsStart = !!atPayment && !!productChoice?.productId && !waitingForPayment;

  /**
   * Send them back to the products if the list no longer offers what they picked.
   *
   * The pick was made against a list answered for a signed-out visitor, and `/v2/products` answers
   * a signed-in one differently — see `isChoiceStale`, which owns the comparison. All the flow does
   * is ask the list again once there is a session (`UserSessionSync` invalidates it on sign-in) and
   * notice that the answer moved.
   *
   * Noticing is worth a step backwards rather than a quiet substitution. The visitor agreed to a
   * product at a price; carrying them on to Stripe would present something they never chose, where
   * the only options left are to pay it or abandon. The product list is one press away, now
   * answered for the account they have just proved, and the rest of the journey is intact. The API
   * refuses the same purchase for the same reason (`checkPriceEligibility`, ADR 0002 in zl-api), so
   * this is not the only thing standing between a visitor and a surprise total — but it is the one
   * that gets there first, before a checkout tab has been opened on a request that cannot succeed.
   *
   * Only the currency moves prices here. No card asks `/v2/products` for a `variant`, so a product
   * resolves to one price for everybody; if one ever does, `useChosenProduct` has to ask for the
   * same variant or an unchanged price would read as a changed one and bounce every purchase of it.
   */
  const { product: chosenProduct, listed, reconcile } = useChosenProduct();
  useEffect(() => {
    // Nothing to reconsider once a session exists: it was created from the choice as it stood, and
    // hauling the visitor out of an open Stripe tab is not a correction.
    if (!atPayment || waitingForPayment || startedCheckoutRef.current) {
      return;
    }
    if (
      isChoiceStale({
        recorded: productChoice?.priceId,
        offered: chosenProduct?.price.priceId,
        answered: listed,
      })
    ) {
      dispatch(advanceSpeedyFlow({ type: 'choiceUnavailable' }));
    }
  }, [atPayment, waitingForPayment, productChoice, chosenProduct, listed, dispatch]);

  /**
   * The guard is per visit to the payment step, not per page.
   *
   * This component never unmounts — it renders `null` when there is no flow — so the ref outlives
   * every close and reopen. Cleared only on the way out of a failed attempt, it stayed true after a
   * purchase went through, and the *next* product gate in the same page session opened with a start
   * button that returned immediately and explained nothing. Anything that is not the payment step —
   * the flow closing included — means this attempt is over.
   */
  useEffect(() => {
    if (!atPayment) {
      startedCheckoutRef.current = false;
    }
  }, [atPayment]);

  /**
   * A checkout session that could not be created.
   *
   * The same answer wherever the request was made from — this component's own deferred start, or a
   * product card's mutation — so both come through here. A card that kept its failure to itself
   * left the tab it had reserved sitting blank and open, and the modal waiting on a payment that
   * had never begun.
   */
  const onCheckoutFailed = useCallback(
    (err: any) => {
      console.error('Could not start checkout', err);
      releaseCheckoutTab();
      setWaitingForPayment(false);
      // Which of the three this is, and why, lives in `checkoutFailureEvent`.
      const event = checkoutFailureEvent(err);
      // The server refused the price, so whatever the cards are showing came from a list that is
      // out of date — it is what produced the bad price. Re-answer it before they land back on it,
      // or they press the same card and get the same refusal.
      if (event === 'choiceUnavailable') {
        queryClient.invalidateQueries({ queryKey: productsQueryKeyPrefix });
      }
      dispatch(advanceSpeedyFlow({ type: event }));
    },
    [releaseCheckoutTab, dispatch, queryClient],
  );

  /**
   * Create the session for a deferred choice and open it.
   *
   * Driven by a click rather than an effect on purpose. The guest path arrives here straight from
   * email verification, and a tab opened outside a user gesture is treated as an unsolicited popup
   * — which would silently demote the visitor to the same-tab fallback and lose the modal.
   *
   * The order of what follows, and why the choice is judged a second time here, is
   * `runDeferredCheckout`'s. All this end has to get right is that the tab is claimed *before* the
   * wait, inside the gesture, so nothing about the popup path depends on how long the answer takes.
   */
  const startDeferredCheckout = useCallback(() => {
    if (!productChoice?.productId || startedCheckoutRef.current) {
      return;
    }
    startedCheckoutRef.current = true;
    // Claim the tab inside this click, before the async round trip.
    reserveCheckoutTab();
    setWaitingForPayment(true);
    // The marker rides back on `co-redirect`, which is how the return routes know they are a
    // popup. No backend change: successPath is already a parameter, and the server's path
    // validation permits a query string.
    const here = window.location.pathname + window.location.search;
    const returnPath = withResumeState(withPopupMarker(here), productChoice);
    runDeferredCheckout(productChoice.priceId, {
      reconcile: () => reconcile().then(({ data, isSuccess }) => ({ offered: data?.price.priceId, answered: isSuccess })),
      createSession: () =>
        postUserProductMutationFn({ ...productChoice, successPath: returnPath, cancelPath: returnPath }).then(
          ({ url }) => url,
        ),
      onUnavailable: () => {
        // No invalidation on the way out, unlike `onCheckoutFailed`: the answer that sent them back
        // is already in the cache, so the list they land on is the corrected one.
        releaseCheckoutTab();
        setWaitingForPayment(false);
        startedCheckoutRef.current = false;
        dispatch(advanceSpeedyFlow({ type: 'choiceUnavailable' }));
      },
      onSession: (url) => openCheckoutTab(url, productChoice),
      onFailed: (err) => {
        // Let them press it again: this attempt is over, and the step they land on may well offer
        // the same button.
        startedCheckoutRef.current = false;
        onCheckoutFailed(err);
      },
    });
  }, [productChoice, reserveCheckoutTab, releaseCheckoutTab, openCheckoutTab, onCheckoutFailed, reconcile, dispatch]);

  /**
   * A member who already named their purchase does not press a second button (ADR 0010).
   *
   * The start button exists for the guest path, which arrives from email verification with no
   * gesture behind it; a member got here by pressing a control that named the product. It also
   * suppresses that button, which would otherwise flash for the frame before this effect runs.
   *
   * Waits on `listed` because eligibility is only known once the product loads, and starting a
   * checkout the API would refuse replaces "you cannot buy this" with a failure. Guests keep their
   * button: opening outside a gesture risks the popup blocker, and the same-tab fallback would cost
   * them a half-finished sign-up.
   *
   * A choice restored from `sf` keeps the button too — `resumedChoice` says so. That is the member
   * whose popup *was* blocked, coming back from a same-tab Stripe redirect: auto-starting again is
   * how cancelling used to be inescapable, and how a payment waiting on its webhook could be
   * charged a second time.
   */
  const autoStartBlocked = useEligibilityBlocked(chosenProduct);
  const willAutoStart = machine?.flow === 'member-purchase' && needsStart && listed && !autoStartBlocked && !resumedChoice;
  useEffect(() => {
    if (!willAutoStart) {
      return;
    }
    startDeferredCheckout();
  }, [willAutoStart, startDeferredCheckout]);

  /**
   * Stamp a card-supplied return path with the popup marker, defaulting to the current page when a
   * card did not supply one.
   */
  const markReturnPath = useCallback(
    (path?: string) => withPopupMarker(path ?? window.location.pathname + window.location.search),
    [],
  );

  /**
   * Social login runs in a tab too, for the same reason checkout does: navigating away would throw
   * the modal, and with it any product already chosen. Returns false if no tab could be claimed,
   * which tells the button to navigate instead.
   */
  const { open: openSso, stop: stopSso } = useSsoPopup({
    onSuccess: () => dispatch(advanceSpeedyFlow({ type: 'ssoSucceeded' })),
    onFailure: () => dispatch(advanceSpeedyFlow({ type: 'ssoFailed' })),
  });

  /**
   * Closing the flow retires whatever tabs it left watching.
   *
   * Both channels tear themselves down on unmount, and this component never unmounts — it renders
   * `null` when there is no flow — so without this their teardown never runs at all. What survives
   * is a 500 ms interval and a `message` listener per open tab, for the life of the page.
   *
   * They outlive the flow that armed them, and the events they carry are dispatched into whatever
   * flow is running when they arrive. Leave a social login open, dismiss the modal, open a flow
   * again later, then close that forgotten tab, and the new flow is told the sign-in it never
   * started has failed.
   */
  useEffect(() => {
    if (visible) {
      return;
    }
    stopSso();
    stopCheckout();
    // Nothing is being waited on any more, and a stale `true` here makes the next flow persistent
    // for a payment that belonged to the last one.
    setWaitingForPayment(false);
  }, [visible, stopSso, stopCheckout]);

  useResumeState();

  /** How long the history was when the app loaded — the baseline `leaveGatedPage` measures against. */
  const [historyLengthAtLoad] = useState(() => (typeof window === 'undefined' ? 0 : window.history.length));

  /*
   * Navigating away closes the flow — see `speedyFlowNavigated`, fed by the router in `Main`.
   *
   * This used to be a `popstate` listener here, which only caught the browser's back button.
   * `route()` uses pushState and emits no event, so every programmatic redirect left the modal
   * floating over a page it had nothing to do with.
   */

  const logged = useSelector((state) => state.user.logged);

  /**
   * Keep the post-auth destination pointing back at what they picked.
   *
   * A visitor who leaves to fetch a magic link finishes authenticating in whichever tab their mail
   * client opens, and that tab knows nothing about this one. The link itself cannot carry the
   * choice: the server builds it as `/login?ml=<token>` with no redirect of its own, and where the
   * visitor lands afterwards comes from this cookie. So the choice rides on the cookie, in the same
   * `sf` form the same-tab Stripe fallback already uses — and lands them on the payment step with
   * it pre-filled rather than back at a product list they have already been through.
   *
   * Only while signed out, matching when the destination is recorded at all. Once they are signed
   * in there is no round trip left to survive, and overwriting it would fight the guard that reads
   * it.
   */
  useEffect(() => {
    if (!visible || !productChoice?.productId || logged) {
      return;
    }
    // This page, not the saved destination. That records the last page they were trying to reach
    // and survives a move to /home on purpose, so building on it sent people back to somewhere they
    // had browsed earlier and left — with a product choice attached that belonged to here.
    savePostAuthDestination(withResumeState(`${window.location.pathname}${window.location.search}`, productChoice));
  }, [visible, productChoice, logged]);

  usePersistPersona();

  useReportFacts(visible, gate);

  const landingPath = useDefaultLandingPath();

  /**
   * Take the visitor off a page that is no use to them without the flow.
   *
   * Back rather than a fixed destination: they wanted out, and out is where they came from.
   *
   * Unless there is no "came from". A link opened straight into a fresh tab has nothing behind it,
   * and `history.back()` there does nothing at all — leaving them on exactly the empty page this
   * exists to rescue them from. `history.length` cannot answer that question: the entry a tab opens
   * on counts, so the length is already above one before the visitor has been anywhere. What does
   * answer it is whether the length has *grown* since the app loaded, because that only happens
   * when a navigation pushed an entry. Replacements — stripping `sf`, for one — leave it alone,
   * which is right: they did not move anybody.
   */
  const leaveGatedPage = useCallback(() => {
    if (window.history.length > historyLengthAtLoad) {
      window.history.back();
      return;
    }
    route(landingPath, true);
  }, [landingPath, historyLengthAtLoad]);

  /**
   * What a closed flow leaves behind, decided in one place.
   *
   * Two things have to happen when the modal goes away, and both are answered by the same question —
   * is the gate satisfied *now*:
   *
   * - **Do the thing they were originally trying to do.** The gate interrupted an intent, so
   *   finishing the flow has to finish that intent too, or someone who signed up in order to follow
   *   a habitat lands back on the page not following it, with nothing to tell them their click was
   *   swallowed. Asking whether the gate is satisfied rather than whether the flow "succeeded"
   *   covers what a success flag would miss — an upgrade resolved in place, a visitor who turned out
   *   to already hold the entitlement — and correctly skips the ones who walked away.
   * - **Take them off a page that is no use to them.** Everything under an `exitOnDismiss` gate is
   *   a blurred placeholder for content this visitor cannot reach.
   *
   * Which makes them exclusive: satisfied means the page works and there is an intent to resume,
   * unsatisfied means there is nothing to resume and nowhere useful to stand.
   *
   * Driven by the flow *becoming* invisible rather than by the dismiss handlers, because the visitor
   * is not the only one who ends a flow. A checkout abandoned in a journey that skipped the product
   * step ends it from inside the machine (`paymentAbandoned` → `finish`), and that used to reach
   * none of this: the modal vanished and left them on the blurred page it had been covering, with no
   * way forward and nothing to press. Anything that reaches `closed` now lands the same way.
   *
   * Which means leaving has to check where it is standing. Navigating away closes the flow too
   * (`speedyFlowNavigated`), and that visitor has already left — sending them "back" from a page
   * they chose to go to would undo the move they just made. The intent above has no such condition:
   * it belongs to the visitor, not to the page it was formed on.
   */
  const gateRef = useRef<GateRequirement | undefined>();
  const openedPathRef = useRef<string | undefined>();
  const wasVisible = useRef(false);
  useEffect(() => {
    if (visible) {
      wasVisible.current = true;
      gateRef.current = gate;
      openedPathRef.current = window.location.pathname;
      return;
    }
    if (!wasVisible.current) {
      return;
    }
    wasVisible.current = false;
    const closedGate = gateRef.current;
    const closedOnItsPage = openedPathRef.current === window.location.pathname;
    gateRef.current = undefined;
    openedPathRef.current = undefined;
    // Taken either way: it belongs to the flow that has just ended, and leaving it behind would run
    // it against the next one.
    const action = takePendingAction();
    if (!closedGate) {
      return;
    }
    // Read the store now rather than subscribing: the user has just changed, and this is the only
    // moment the answer matters.
    if (satisfiesGate(closedGate, selectSpeedyFlowUser(store.getState()))) {
      action?.();
      return;
    }
    if (closedGate.exitOnDismiss && closedOnItsPage) {
      leaveGatedPage();
    }
  }, [visible, gate, store, leaveGatedPage]);

  /**
   * Dismissal is a request, not an ending: a dismissal that still owes a persona question keeps the
   * modal open, and leaving mid-question would throw the question away. So this only asks, and the
   * effect above deals with whatever the machine decides.
   */
  const dismiss = useCallback(() => {
    dispatch(advanceSpeedyFlow({ type: 'dismissed' }));
  }, [dispatch]);

  /** The confirmation card's own way out — an ending outright, having nothing left to ask. */
  const finishFromCompletion = useCallback(() => {
    dispatch(closeSpeedyFlow());
  }, [dispatch]);

  /**
   * How many modals a step currently has open over the flow. While there is one, the flow stops
   * listening for its own dismissal — see `useCoveringModal` for why a backdrop click would
   * otherwise close both.
   *
   * Counted, so two overlapping modals cannot have the first to close re-enable dismissal
   * underneath the second.
   */
  const [coveringModals, setCoveringModals] = useState(0);
  const registerCoveringModal = useCallback(() => {
    setCoveringModals((count) => count + 1);
    return () => setCoveringModals((count) => count - 1);
  }, []);

  if (!visible || !machine) {
    return null;
  }

  const { step, completionVariant, unconfirmed } = machine;
  // Neither asks the visitor for anything — one reports on a checkout happening elsewhere, the
  // other confirms it is done — so both stay popups rather than taking over the page as a sheet.
  const popupOnMobile = step === 'await-payment' || step === 'completion';
  // Completion has its own CTA and nothing left to ask, so it is dismissable even on a persistent
  // gate — by then the visitor either has what they came for or has declined it.
  //
  // A checkout open in another tab makes any flow persistent for as long as it is open. The popup
  // reports back here, and a flow that has been dismissed has no machine left to report to: a
  // payment that went through would be swallowed, leaving someone who has just been charged with
  // no confirmation and no completion card. Escape is far too cheap a way to cause that. This is
  // the window between the tab opening and it reporting — before that there is nothing at risk,
  // and after it the machine has already moved on.
  //
  // A modal opened by a step over the top of the flow does the same, for as long as it is open: a
  // click on its backdrop would otherwise dismiss the flow underneath it as well as the modal
  // itself. See `useCoveringModal`.
  const persistent = (!!gate?.persistent && step !== 'completion') || waitingForPayment || coveringModals > 0;

  const renderStep = () => {
    switch (step) {
      case 'persona':
        return <PersonaStep />;
      case 'sign-up':
        return (
          <AuthPopupProvider value={{ open: openSso, params: { ppt } }}>
            <SignUpStep />
          </AuthPopupProvider>
        );
      case 'log-in':
        return (
          <AuthPopupProvider value={{ open: openSso, params: { ppt } }}>
            <LogInStep />
          </AuthPopupProvider>
        );
      case 'verify-email':
        return <VerifyEmailStep />;
      case 'choose-product':
        return (
          <ChooseProductStep
            interception={
              machine.hasAccount
                ? {
                    onCheckoutUrl: openCheckoutTab,
                    reserveCheckoutTab,
                    markReturnPath,
                    onCheckoutFailed,
                    previousSelection: productChoice,
                  }
                : { onSelectProduct, previousSelection: productChoice }
            }
          />
        );
      case 'await-payment':
        return (
          <AwaitPaymentStep
            unconfirmed={unconfirmed}
            waiting={waitingForPayment}
            needsStart={needsStart && !willAutoStart}
            onStart={startDeferredCheckout}
            onRetry={retry}
            onChooseAnother={() => dispatch(advanceSpeedyFlow({ type: 'checkoutRefused' }))}
          />
        );
      case 'completion':
        return <CompletionStep variant={completionVariant ?? 'account'} onDone={finishFromCompletion} />;
      default:
        return null;
    }
  };

  return (
    <CoveringModalContext.Provider value={registerCoveringModal}>
      <SpeedyFlowShell
        onDismiss={dismiss}
        popupOnMobile={popupOnMobile}
        stepLabel={stepLabels[step]}
        persistent={persistent}
      >
        {renderStep()}
      </SpeedyFlowShell>
    </CoveringModalContext.Provider>
  );
};

export default SpeedyFlow;
